import {throwError as observableThrowError,  Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { tap, catchError } from 'rxjs/operators';
import {JsonService} from "../../../services/core/json.service";
import {SubcontractorApiResources} from "./subcontractor.constants";

@Injectable({
  providedIn: 'root'
})
export class SubcontractorDataService {
  private baseURL;

  constructor(
    private http: HttpClient,
    private jsonService: JsonService,
  ) {
    this.baseURL = SubcontractorApiResources;
  }

  list(model: any = null): Observable<any> {
    const endPoint = `${this.baseURL.list}`;
    return this.http.post<any>(endPoint, model)
                    .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }

  getList(requestparam:any = {}): Observable<any> {
       let params = new HttpParams();
    for (const key in requestparam) {
      if (requestparam.hasOwnProperty(key)) {
        params = params.append(key, requestparam[key]);
      }
    }
    const endPoint = `${this.baseURL.list}`;
    return this.http.get<any>(endPoint+'/list', {params})
                    .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }

  create(model: any): Observable<any> {
    const endPoint = `${this.baseURL.create}`;
    return this.http.post<any>(endPoint, model)
      .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }

  copySubcontractor(id: any, model: any): Observable<any> {
    const endPoint = `${this.baseURL.copy}/${id}`;
    return this.http.post<any>(endPoint, model)
      .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }

  detail(id:any): Observable<any> {
    const endPoint = `${this.baseURL.detail}/${id}`;
    return this.http.get<any>(endPoint)
      .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }

  update(id:any, model: any): Observable<any> {
    const endPoint = `${this.baseURL.update}/${id}`;
    return this.http.post<any>(endPoint, model)
      .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }

  delete(id:any): Observable<any> {
    const endPoint = `${this.baseURL.delete}/${id}`;
    return this.http.delete<any>(endPoint)
      .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }

  // Bulk delete: DELETE subcontractors/delete/multiple with { subcontractor_ids: [...] }
  deleteMultiple(subcontractorIds: any[]): Observable<any> {
    const endPoint = `${this.baseURL.deleteMultiple}`;
    return this.http.request<any>('delete', endPoint, { body: { subcontractor_ids: subcontractorIds } })
      .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }

  // Pre-check which of the selected subcontractors can be deleted (and why not, for the rest)
  deleteAvailability(subcontractorIds: any[]): Observable<any> {
    const endPoint = `${this.baseURL.deleteAvailability}`;
    return this.http.post<any>(endPoint, { subcontractor_ids: subcontractorIds })
      .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }

  onAttach(model:any): Observable<any> {
    const endPoint = `${this.baseURL.attachSkill}`;
    return this.http.post<any>(endPoint, model)
      .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }

  onDetach(model:any): Observable<any> {
    const endPoint = `${this.baseURL.detachSkill}`;
    return this.http.post<any>(endPoint, model)
      .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }

  errorHandler(error: HttpErrorResponse) {
    return observableThrowError(error.error || 'Server Error');
  }

  // Import Methods
  downloadSampleFile(): Observable<any> {
    const endPoint = `${this.baseURL.downloadSample}`;
    return this.http.get(endPoint, { responseType: 'blob' })
      .pipe(catchError(this.errorHandlerMessage));
  }

  uploadImportFile(file: File): Observable<any> {
    const endPoint = `${this.baseURL.uploadImport}`;
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<any>(endPoint, formData)
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  validateImportData(data: any): Observable<any> {
    const endPoint = `${this.baseURL.validateImport}`;
    return this.http.post<any>(endPoint, data)
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  executeImport(data: any): Observable<any> {
    const endPoint = `${this.baseURL.executeImport}`;
    return this.http.post<any>(endPoint, data)
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  getImportJobStatus(jobId: string): Observable<any> {
    const endPoint = `${this.baseURL.importStatus}/${jobId}`;
    return this.http.get<any>(endPoint)
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  // Import History Methods (reuse existing with module_id=9)
  getImportHistory(params: any = {}): Observable<any> {
    const endPoint = `${this.baseURL.importHistory}`;
    let httpParams = new HttpParams();
    
    // Always add subcontractor module_id
    httpParams = httpParams.set('module_id', '9');
    
    Object.keys(params).forEach(key => {
      if (params[key] !== null && params[key] !== undefined) {
        httpParams = httpParams.set(key, params[key]);
      }
    });
    
    return this.http.get<any>(endPoint, { params: httpParams })
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  getImportHistoryDetails(id: number): Observable<any> {
    const endPoint = `${this.baseURL.importHistory}/${id}`;
    return this.http.get<any>(endPoint)
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  deleteImportHistory(id: number): Observable<any> {
    const endPoint = `${this.baseURL.importHistory}/${id}`;
    return this.http.delete<any>(endPoint)
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  getImportStats(params: any = {}): Observable<any> {
    const endPoint = `${this.baseURL.importHistory}/stats`;
    let httpParams = new HttpParams();
    
    // Always add subcontractor module_id
    httpParams = httpParams.set('module_id', '9');
    
    Object.keys(params).forEach(key => {
      if (params[key] !== null && params[key] !== undefined) {
        httpParams = httpParams.set(key, params[key]);
      }
    });
    
    return this.http.get<any>(endPoint, { params: httpParams })
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  errorHandlerMessage(error: HttpErrorResponse) {
    return observableThrowError(error.error || 'Server Error');
  }

  /**
   * Create or update profile image for subcontractor
   */
  createProfileImage(subcontractorId: any, payload: any): Observable<any> {
    const endPoint = `${this.baseURL.update}/${subcontractorId}/profile-image`;
    return this.http.post<any>(endPoint, payload)
      .pipe(tap(data => this.jsonService.doStringify(data)), catchError(this.errorHandlerMessage));
  }
}
